/**
 * SSE-over-fetch parser (supports POST streams like Ask Your Data).
 *
 * The frames carry no `event:` line — the discriminator is the `type` field
 * inside the JSON — and a frame that is only a comment (`: keepalive`) parses
 * to nothing and is ignored, which is what keeps a long question's connection
 * open without confusing the reducer.
 *
 * Used by Core event streams. Scout uses persisted JSON conversations.
 */
import { getToken } from "./api";

export function streamSse(
  url: string,
  init: RequestInit | undefined,
  onEvent: (type: string, data: Record<string, unknown>) => void,
): { close: () => void } {
  const ctrl = new AbortController();
  (async () => {
    const res = await fetch(url, {
      ...init,
      signal: ctrl.signal,
      headers: {
        Accept: "text/event-stream",
        ...(init?.body ? { "Content-Type": "application/json" } : {}),
        ...(getToken() ? { Authorization: `Bearer ${getToken()}` } : {}),
        ...((init?.headers as Record<string, string>) ?? {}),
      },
    });
    if (!res.ok || !res.body) throw new Error(`SSE ${res.status}`);
    const reader = res.body.getReader();
    const dec = new TextDecoder();
    let buf = "";
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buf += dec.decode(value, { stream: true });
      let idx: number;
      while ((idx = buf.indexOf("\n\n")) >= 0) {
        const chunk = buf.slice(0, idx);
        buf = buf.slice(idx + 2);
        let ev = "message";
        const data: string[] = [];
        for (const line of chunk.split("\n")) {
          if (line.startsWith("event:")) ev = line.slice(6).trim();
          else if (line.startsWith("data:")) data.push(line.slice(5).trim());
        }
        try { onEvent(ev, JSON.parse(data.join("") || "{}")); } catch { /* heartbeat */ }
      }
    }
    onEvent("done", {});
  })().catch((e) => {
    if ((e as Error).name !== "AbortError") onEvent("error", { message: String(e) });
  });
  return { close: () => ctrl.abort() };
}
